/**
 * Task estimated duration — stored as minutes (`estimatedDurationMinutes`).
 * Legacy migration lives in `migrations/legacyEstimatedDuration.js`.
 */

export const MIN_DURATION_MINUTES = 15;

const MS_PER_MINUTE = 60 * 1000;

export {
  migrateSprintEstimatedDuration,
  normalizeTaskEstimatedDuration,
} from './migrations/legacyEstimatedDuration.js';

/**
 * Effective duration in whole minutes, or null if unset (normalized tasks).
 * @param {Object} task
 * @returns {number|null}
 */
export function getEffectiveDurationMinutes(task) {
  if (!task) return null;
  const m = task.estimatedDurationMinutes;
  if (m != null && Number.isFinite(m) && m > 0) {
    return Math.round(m);
  }
  return null;
}

export function minutesToMs(minutes) {
  return minutes * MS_PER_MINUTE;
}

/**
 * Fastest~longest estimated duration. A single value when the two ends match.
 * @param {number} minMinutes
 * @param {number} maxMinutes
 * @returns {string}
 */
export function formatDurationRange(minMinutes, maxMinutes) {
  const min = Math.max(0, Math.round(Number(minMinutes) || 0));
  const max = Math.max(min, Math.round(Number(maxMinutes) || 0));
  const minLabel = min > 0 ? formatDurationMinutes(min) : '0 min';
  const maxLabel = max > 0 ? formatDurationMinutes(max) : '0 min';
  if (min === max) return minLabel;
  return `${minLabel} ~ ${maxLabel}`;
}

/**
 * Human-readable duration for UI and validation messages.
 * @param {number} minutes
 * @returns {string}
 */
export function formatDurationMinutes(minutes) {
  if (minutes == null || !Number.isFinite(minutes) || minutes <= 0) return '';
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (m < 1440) {
    return rem === 0 ? `${h} hr` : `${h} hr ${rem} min`;
  }
  const d = Math.floor(m / 1440);
  const afterDays = m - d * 1440;
  const hr = Math.floor(afterDays / 60);
  const min = afterDays % 60;
  const parts = [`${d} day${d !== 1 ? 's' : ''}`];
  if (hr > 0) parts.push(`${hr} hr`);
  if (min > 0) parts.push(`${min} min`);
  return parts.join(' ');
}

/**
 * Human-readable duration from a millisecond delta (validation copy).
 * @param {number} ms
 * @returns {string}
 */
export function formatMsDuration(ms) {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return '';
  const minutes = Math.max(1, Math.ceil(ms / MS_PER_MINUTE));
  return formatDurationMinutes(minutes);
}
